import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';
import { Check, Circle, ArrowRight, Dices } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function OnboardingBanner({ onDraft, onManageTeam, onDraftRandom, onFinalize, currentTeam, leagueSettings }) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const slots = currentTeam?.slots || [];
  const entityCount = slots.length;
  const hasCaptain = slots.some(s => s.is_captain);
  
  // League-specific total (from slot_config or settings).
  // v5.7.51: defaults updated 7→6 categories + cap 21→18 to match the
  // post-fictional-character era. Stale defaults were leaking into the
  // onboarding checklist ("Fill your roster (21 people)") even for new
  // leagues created without a custom slot_config.
  const slotConfig = leagueSettings?.slot_config || {};
  const hasCustomConfig = Object.keys(slotConfig).length > 0;
  const totalNeeded = hasCustomConfig
    ? Object.values(slotConfig).reduce((a, b) => a + b, 0)
    : Math.min((leagueSettings?.allowed_categories?.length || 6) * (leagueSettings?.required_per_category || 3), 18);

  const finalized = !!currentTeam?.finalized;
  const steps = [
    // 2026-10-12 Activation Fix Pack — go live with 1+ picks; the captain is
    // confirmed by the forced dialog at Finalize, so it's no longer a step.
    { id: 'draft_first', label: 'Draft your first person', complete: entityCount >= 1 },
    { id: 'go_live', label: 'Go live — tap Finalize (1 pick is enough)', complete: finalized },
    { id: 'draft_full', label: `Fill all ${totalNeeded} slots — free, even after going live`, complete: entityCount >= totalNeeded },
  ];

  const completedCount = steps.filter(s => s.complete).length;
  const completion = (completedCount / steps.length) * 100;
  
  if (completion >= 100) return null;
  // A live team only has "fill the open slots" left — the Stage's own
  // "Draft Room — Fill N Open Slots (free)" CTA covers that; stop nagging.
  if (finalized) return null;

  const nextStep = steps.find(s => !s.complete);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="mx-4 mb-4 bg-card border border-[hsl(var(--accent)/0.3)] rounded-2xl p-4"
        data-testid="onboarding-banner"
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">Getting Started</h3>
          <button className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setDismissed(true)}>
            Dismiss
          </button>
        </div>

        <Progress value={completion} className="h-2 mb-3" />
        <p className="text-xs text-muted-foreground mb-3">
          {Math.round(completion)}% complete • {entityCount} people drafted
        </p>

        <div className="space-y-2 mb-3">
          {steps.map((step) => (
            <div key={step.id} className={`flex items-center gap-2 text-xs ${step.complete ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
              {step.complete ? (
                <Check className="h-3.5 w-3.5 text-green-500" />
              ) : (
                <Circle className="h-3.5 w-3.5 text-[hsl(var(--accent))]" />
              )}
              <span>{step.label}</span>
            </div>
          ))}
        </div>

        {nextStep && (
          <Button size="sm" className="w-full" onClick={() => {
            if (nextStep.id === 'go_live') onFinalize?.();
            else onDraft?.();
          }} data-testid="onboarding-cta">
            {nextStep.id === 'go_live' ? `Go live with ${entityCount} pick${entityCount === 1 ? '' : 's'}` : 'Go to Draft Room'}
            <ArrowRight className="h-3.5 w-3.5 ml-2" />
          </Button>
        )}
        {nextStep && nextStep.id === 'go_live' && (
          <Button size="sm" variant="outline" className="w-full mt-2" onClick={onDraft} data-testid="onboarding-keep-drafting">
            Keep drafting
          </Button>
        )}
        {/* 2026-09-07 (owner request) — Draft Random lives right under "Go to
            Draft Room" (was buried below the arena). Open slots = totalNeeded − drafted. */}
        {nextStep && currentTeam?.id && onDraftRandom && (
          <Button
            size="sm"
            variant="outline"
            className="w-full mt-2 border-dashed"
            onClick={onDraftRandom}
            data-testid="onboarding-draft-random-btn"
          >
            <Dices className="h-3.5 w-3.5 mr-2" />
            Draft Random — fill {totalNeeded - entityCount} slot{totalNeeded - entityCount === 1 ? '' : 's'}
          </Button>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
